import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, ArrowUp, ArrowDown, ExternalLink, FolderOpen } from 'lucide-react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { useText } from '../../lib/siteContent';
import { PROJECT_CATEGORIES, categoryLabel } from '../../lib/projects';
import ImageUploader from '../../components/admin/ImageUploader';

const EMPTY = { title: '', category: 'weddings', location: '', coverUrl: '', intro: '', direction: '', palette: [], spaces: '', partners: '', images: [], published: true, order: 0 };
const lines = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const moveItem = (arr, i, d) => { const a = [...arr]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; };
const sq = 'w-8 h-8 flex items-center justify-center border border-[#111111]/20 hover:border-[#111111] bg-white disabled:opacity-30';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">{label}</span>
    {children}
  </label>
);

const PaletteEditor = ({ value, onChange }) => (
  <div className="space-y-3" data-testid="project-form-palette">
    {value.map((c, i) => (
      <div key={i} className="flex items-center gap-3">
        <input type="color" value={c.hex} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))} className="w-10 h-10 border border-[#111111]/20 p-0.5 bg-white shrink-0" aria-label="Couleur" data-testid={`palette-color-${i}`} />
        <input value={c.name} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Nom (ex. Ivoire)" className="luxury-input-light flex-1 min-w-0" data-testid={`palette-name-${i}`} />
        <button type="button" className={sq} onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Retirer la couleur"><X className="w-3.5 h-3.5" /></button>
      </div>
    ))}
    {value.length < 12 && (
      <button type="button" onClick={() => onChange([...value, { name: '', hex: '#d9cfc1' }])} className="text-[11px] uppercase tracking-[0.2em] text-[#111111] inline-flex items-center gap-2 hover:text-[#6B6B6B]" data-testid="palette-add"><Plus className="w-3.5 h-3.5" /> Ajouter une couleur</button>
    )}
  </div>
);

const ImagesEditor = ({ value, onChange }) => (
  <div data-testid="project-form-images">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
      {value.map((src, i) => (
        <div key={`${src}-${i}`} className="relative border border-[#111111]/10 bg-[#F5F5F5]">
          <img src={resolveImageUrl(src)} alt="" className="w-full h-24 object-cover" />
          <div className="absolute top-1 right-1 flex gap-1">
            <button type="button" className={sq} disabled={i === 0} onClick={() => onChange(moveItem(value, i, -1))} aria-label="Avancer"><ArrowUp className="w-3 h-3" /></button>
            <button type="button" className={sq} disabled={i === value.length - 1} onClick={() => onChange(moveItem(value, i, 1))} aria-label="Reculer"><ArrowDown className="w-3 h-3" /></button>
            <button type="button" className={`${sq} hover:text-red-500`} onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Retirer l'image" data-testid={`project-image-remove-${i}`}><X className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
    </div>
    <ImageUploader value="" onChange={(v) => v && onChange([...value, v])} testId="project-form-image-add" label="Ajouter une image" />
  </div>
);

const ProjectForm = ({ initial, onClose, onSaved }) => {
  const t = useText();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const input = (k) => ({ value: form[k] ?? '', onChange: (e) => set(k)(e.target.value), className: 'luxury-input-light', 'data-testid': `project-form-${k}` });

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form, title: form.title.trim(), location: form.location || null, coverUrl: form.coverUrl || null, spaces: lines(form.spaces), partners: lines(form.partners), palette: form.palette.filter((c) => c.name.trim()), order: Number(form.order) || 0 };
      delete payload.id; delete payload.slug; delete payload.createdAt;
      if (form.id) await api.put(`/admin/projects/${form.id}`, payload); else await api.post('/admin/projects', payload);
      toast.success('Réalisation enregistrée');
      onSaved();
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur lors de l’enregistrement'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl p-6 sm:p-10 border-t-4 border-[#111111] relative my-6" data-testid="project-form">
        <button type="button" onClick={onClose} className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]" aria-label="Fermer"><X className="w-5 h-5" /></button>
        <h3 className="text-2xl font-serif mb-8 text-[#111111]">{form.id ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</h3>
        <div className="space-y-6">
          <Field label="Titre *"><input {...input('title')} required /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Catégorie">
              <select {...input('category')}>{PROJECT_CATEGORIES.map((k) => <option key={k} value={k}>{categoryLabel(t, k)}</option>)}</select>
            </Field>
            <Field label="Lieu (optionnel)"><input {...input('location')} placeholder="Château de…, Belgique" /></Field>
          </div>
          <Field label="Image de couverture"><ImageUploader value={form.coverUrl} onChange={set('coverUrl')} testId="project-form-cover" /></Field>
          <Field label="Introduction"><textarea rows={4} {...input('intro')} /></Field>
          <Field label="Direction artistique"><textarea rows={5} {...input('direction')} /></Field>
          <Field label="Palette"><PaletteEditor value={form.palette} onChange={set('palette')} /></Field>
          <Field label="Espaces mis en scène (un par ligne)"><textarea rows={4} {...input('spaces')} /></Field>
          <Field label="Partenaires (un par ligne)"><textarea rows={4} {...input('partners')} /></Field>
          <Field label="Galerie (images en pleine largeur, dans l’ordre)"><ImagesEditor value={form.images} onChange={set('images')} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
            <Field label="Ordre d’affichage"><input type="number" {...input('order')} /></Field>
            <label className="flex items-center gap-3 text-sm text-[#111111] pb-3">
              <input type="checkbox" checked={form.published} onChange={(e) => set('published')(e.target.checked)} className="w-4 h-4 accent-[#111111]" data-testid="project-form-published" />
              Publiée sur le site
            </label>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-10">
          <button type="button" onClick={onClose} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/30 hover:bg-[#F5F5F5]">Annuler</button>
          <button type="submit" disabled={saving} data-testid="project-form-submit" className="flex-1 min-w-[140px] px-6 py-3 text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] disabled:opacity-40">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
};

const ProjectsManager = () => {
  const t = useText();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get('/admin/projects').then(({ data }) => setItems(data)).catch(() => { setItems([]); toast.error('Erreur de chargement'); });
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title} » ?`)) return;
    try { await api.delete(`/admin/projects/${p.id}`); toast.success('Réalisation supprimée'); load(); } catch { toast.error('Erreur'); }
  };
  const edit = (p) => setEditing({ ...EMPTY, ...p, location: p.location || '', coverUrl: p.coverUrl || '', spaces: (p.spaces || []).join('\n'), partners: (p.partners || []).join('\n') });

  return (
    <div data-testid="admin-projects-manager">
      <div className="flex flex-wrap justify-between items-center gap-6 mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Portfolio</span>
          <h2 className="text-4xl font-serif text-[#111111]">Réalisations</h2>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl">Chaque réalisation publiée a sa propre page sur /realisations. Le carrousel de l’accueil reste géré dans l’onglet Galerie.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, order: items?.length || 0 })} data-testid="project-add-btn"
          className="w-full sm:w-auto justify-center flex items-center gap-3 px-6 py-3 border border-[#111111] text-xs uppercase tracking-[0.3em] hover:bg-[#333333] hover:text-white transition-all whitespace-nowrap">
          <Plus className="w-4 h-4" /> Ajouter une réalisation
        </button>
      </div>
      {!items ? <p className="text-center text-[#6B6B6B] py-20">Chargement…</p> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <FolderOpen className="w-10 h-10 text-[#6B6B6B] mx-auto mb-4" strokeWidth={1} />
          <p className="text-[#6B6B6B]">Aucune réalisation : la page Réalisations affiche la galerie.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} className="border border-[#111111]/10 bg-white" data-testid={`project-item-${p.id}`}>
              <div className="h-40 bg-[#F5F5F5]">{p.coverUrl && <img src={resolveImageUrl(p.coverUrl)} alt="" className="w-full h-full object-cover" />}</div>
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">{categoryLabel(t, p.category)} · Ordre {p.order}{!p.published && ' · Brouillon'}</p>
                <h3 className="font-serif text-xl text-[#111111] mt-2 break-words">{p.title}</h3>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => edit(p)} data-testid={`project-edit-${p.id}`} className="inline-flex items-center gap-2 px-3 py-2 border border-[#111111]/20 text-[10px] uppercase tracking-[0.2em] hover:border-[#111111]"><Pencil className="w-3 h-3" /> Modifier</button>
                  {p.published && <a href={`/realisations/${p.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-[#111111]/20 text-[10px] uppercase tracking-[0.2em] hover:border-[#111111]"><ExternalLink className="w-3 h-3" /> Voir</a>}
                  <button onClick={() => del(p)} data-testid={`project-delete-${p.id}`} className="inline-flex items-center gap-2 px-3 py-2 border border-[#111111]/20 text-[10px] uppercase tracking-[0.2em] hover:border-red-500 hover:text-red-500"><Trash2 className="w-3 h-3" /> Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <ProjectForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

export default ProjectsManager;
